import { BackgroundFit } from "@redotech/cockroachdb-codegen/congodb/background-fit";
import {
  ButtonElementSchema,
  ColumnElementSchema,
  DiscountCodeElementSchema,
  fontWeights,
  ImageElementSchema,
  InputElementSchema,
  RichTextElementSchema,
  TextToSubscribeButtonElementSchema,
  TextToSubscribeDescriptionElementSchema,
} from "@redotech/redo-content-builder-common/element-schema";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  alignmentHorizontalValues,
  alignmentVerticalValues,
  audienceTypes,
  deviceOptions,
  imageDirections,
  LayoutType,
  SignUpFormGoal,
  signupFormSiteTypes,
  targetingTypes,
  teaserVisibility,
  URLMatchingType,
  URLVisibilityType,
} from "./signup-form";

const BorderStrokeStyleDefinitionSchema_ = z.object({
  color: z.string(),
  weightPx: z.number(),
});
const BorderStrokeStyleDefinitionSchema: z.ZodType<BorderStrokeStyleDefinition> =
  BorderStrokeStyleDefinitionSchema_;
export interface BorderStrokeStyleDefinition extends z.infer<
  typeof BorderStrokeStyleDefinitionSchema_
> {}

const BorderStyleDefinitionSchema_ = z.object({
  cornerRadiusPx: z.number(),
  stroke: BorderStrokeStyleDefinitionSchema,
});
const BorderStyleDefinitionSchema: z.ZodType<BorderStyleDefinition> =
  BorderStyleDefinitionSchema_;
export interface BorderStyleDefinition extends z.infer<
  typeof BorderStyleDefinitionSchema_
> {}

const elementStyleSchema_ = z.object({
  font: z.object({
    fontFamily: z.string(),
    fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
    fontSizePx: z.number(),
  }),
  textColor: z.string(),
  backgroundColor: z.string().nullish(),
  border: z.object({
    cornerRadiusPx: z.number(),
    stroke: BorderStrokeStyleDefinitionSchema,
  }),
  paddingPx: z.number(),
});
const elementStyleSchema: z.ZodType<ElementStyle> = elementStyleSchema_;
export interface ElementStyle extends z.infer<typeof elementStyleSchema_> {}

const inputStyleSchema = elementStyleSchema_.extend({ errorColor: z.string() });

const buttonStyleSchema = elementStyleSchema;

const TeaserSchema_ = z.object({
  enabled: z.boolean(),
  text: z.string(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSizePx: z.number(),
  positionHorizontal: createEnum(alignmentHorizontalValues, {
    enumName: "AlignmentHorizontalValue",
  }),
  positionVertical: createEnum(alignmentVerticalValues, {
    enumName: "AlignmentVerticalValue",
  }),
  backgroundColor: z.string(),
  textColor: z.string(),
  cornerRadiusPx: z.number(),
  borderStroke: BorderStrokeStyleDefinitionSchema,
  iconVisible: z.boolean(),
  visibility: createEnum(teaserVisibility, { enumName: "TeaserVisibility" }),
});
const TeaserSchema: z.ZodType<Teaser> = TeaserSchema_;
export interface Teaser extends z.infer<typeof TeaserSchema_> {}

const ElementSchema = z.union([
  RichTextElementSchema,
  ButtonElementSchema,
  InputElementSchema,
  ImageElementSchema,
  ColumnElementSchema,
  DiscountCodeElementSchema,
  TextToSubscribeButtonElementSchema,
  TextToSubscribeDescriptionElementSchema,
]);

const FormStepSchema_ = z.object({ elements: z.array(ElementSchema) });
const FormStepSchema: z.ZodType<FormStep> = FormStepSchema_;
export interface FormStep extends z.infer<typeof FormStepSchema_> {}

// NOTE: there is no way to enable/disable or configure overrides in the signup form builder.
// Whatever is in the database for a form is unchangeable, which may be causing bugs or confusion.
// Not sure if we should remove overrides entirely or update the builder.
export const brandKitOverridesSchema = z.object({
  buttons: z.object({
    enabled: z.boolean(),
    primary: buttonStyleSchema,
    secondary: buttonStyleSchema,
    tertiary: buttonStyleSchema,
  }),
  inputs: inputStyleSchema.extend({ enabled: z.boolean() }),
});
export type BrandKitOverrides = z.infer<typeof brandKitOverridesSchema>;

const signupFormSchema_ = z
  .object({
    name: z.string(),
    goal: createEnum(SignUpFormGoal, { enumName: "SignUpFormGoal" }),
    theme: z.object({
      overrides: brandKitOverridesSchema,
      border: BorderStyleDefinitionSchema,
      closeButton: z.object({
        color: z.string(),
        background: z.object({ enabled: z.boolean(), color: z.string() }),
      }),
      overlay: z.object({
        color: z.string(),
        opacityPercent: z.number(),
        popupOpacityPercent: z.number().optional(),
      }),
    }),
    layout: z.object({
      type: createEnum(LayoutType, { enumName: "LayoutType" }),
      backgroundColor: z.string(),
      image: z.object({
        style: createEnum(imageDirections, { enumName: "ImageDirection" }),
        url: z.string(),
        hideOnMobile: z.boolean(),
        altText: z.string().optional(),
        backgroundFit: z.nativeEnum(BackgroundFit).optional(),
      }),
    }),
    discount: z.object({
      enabled: z.boolean(),
      discountId: z.string().optional(),
    }),
    behavior: z.object({
      appearsOn: createEnum(deviceOptions, { enumName: "DeviceOption" }),
      appearsOnSite: createEnum(signupFormSiteTypes, {
        enumName: "SignupFormSiteType",
      }),
      subscriberTags: z.array(z.string()),
      display: z.object({
        timeOnPage: z.object({ enabled: z.boolean(), seconds: z.number() }),
        exitIntent: z.object({ enabled: z.boolean() }),
      }),
      frequencyCapHours: z.number().min(1).max(720).optional(),
      audience: createEnum(audienceTypes, { enumName: "AudienceType" }),
      campaignUtms: z.array(z.string()),
      targeting: createEnum(targetingTypes, { enumName: "TargetingType" }),
      embeddedCode: z.string(),
      urlVisibility: z.object({
        type: createEnum(URLVisibilityType, { enumName: "URLVisibilityType" }),
        urls: z.array(z.string()),
        matchingType: createEnum(URLMatchingType, {
          enumName: "URLMatchingType",
        }),
      }),
    }),
    teaser: TeaserSchema,
    pdpButton: ButtonElementSchema.optional(),
    steps: z.array(FormStepSchema),
  })
  .transform((form) => {
    // BIS forms always display on all devices (appearsOn is ignored during rendering),
    // so normalize the value to "desktop & mobile" regardless of what's stored in the DB.
    if (form.goal === SignUpFormGoal.BACK_IN_STOCK) {
      return {
        ...form,
        behavior: { ...form.behavior, appearsOn: "desktop & mobile" as const },
      };
    }
    return form;
  });
export const signupFormSchema: z.ZodType<SignupForm> = signupFormSchema_;
export interface SignupForm extends z.infer<typeof signupFormSchema_> {}

const signupFormWithAnalyticsSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  form: signupFormSchema,
  analytics: z.object({ views: z.number(), submissions: z.number() }),
  publishedAt: z.date().nullish(),
  createdAt: z.date(),
  updatedAt: z.date().optional(),
});
export const signupFormWithAnalyticsSchema: z.ZodType<SignupFormWithAnalytics> =
  signupFormWithAnalyticsSchema_;
export interface SignupFormWithAnalytics extends z.infer<
  typeof signupFormWithAnalyticsSchema_
> {}
